import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, LineChart, Line, ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import { useAuth } from "../contexts/AuthContext";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Août","Sep","Oct","Nov","Déc"];

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtShort(n) { if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + "k"; return String(n || 0); }

// Historique réel — vient de GET /api/dashboard/chart/:companyId (CA encaissé
// réel + dépenses réelles par mois, même endpoint déjà utilisé par
// Dashboard.jsx). Remplace l'ancienne fabrication par seededRandom() qui
// produisait la même courbe pour n'importe quelle société à CA identique.
function mapRealHistory(chartData) {
  return (chartData || []).map(d => ({
    month: d.m,
    revenue: d.ca,
    expenses: d.dep,
    profit: d.ca - d.dep,
  }));
}

// Tendance réelle calculée sur l'historique réel — taux de croissance moyen
// mois/mois observé sur les données réelles (pas un multiplicateur fixe
// arbitraire). Les scénarios optimiste/pessimiste restent des hypothèses
// explicites appliquées PAR-DESSUS cette tendance réelle, pas une donnée
// maquillée en résultat.
function computeRealGrowthRate(historicalData) {
  const revenues = historicalData.map(d => d.revenue).filter(v => v > 0);
  if (revenues.length < 2) return 1.0;
  const ratios = [];
  for (let i = 1; i < revenues.length; i++) {
    if (revenues[i - 1] > 0) ratios.push(revenues[i] / revenues[i - 1]);
  }
  if (ratios.length === 0) return 1.0;
  const avg = ratios.reduce((s, r) => s + r, 0) / ratios.length;
  // Borné pour éviter qu'un pic isolé ne produise une extrapolation absurde
  return Math.max(0.85, Math.min(1.15, avg));
}

function buildForecast(historicalData, scenario, months) {
  const validData = historicalData.length > 0 ? historicalData : [{ revenue: 0, expenses: 0 }];
  const avgRevenue = validData.reduce((s, d) => s + d.revenue, 0) / validData.length;
  const avgExpenses = validData.reduce((s, d) => s + d.expenses, 0) / validData.length;
  const realGrowth = computeRealGrowthRate(historicalData);
  const scenarioAdjust = scenario === "optimiste" ? 1.08 : scenario === "pessimiste" ? 0.92 : 1.0;

  const now = new Date();
  return Array.from({ length: months }, (_, i) => {
    const monthIdx = (now.getMonth() + 1 + i) % 12;
    const factor = Math.pow(realGrowth, i + 1) * scenarioAdjust;
    const revenue = Math.round(avgRevenue * factor);
    const expenses = Math.round(avgExpenses);
    const profit = revenue - expenses;
    return {
      month: MONTHS[monthIdx],
      revenue,
      expenses,
      profit,
      upper: Math.round(revenue * 1.12),
      lower: Math.round(revenue * 0.88),
    };
  });
}

// Synthèse calculée à partir des vraies données (pas de génération IA) —
// chaque phrase reflète un fait vérifiable sur CETTE société, jamais un
// texte fixe. Voir aiKey.js/aiUsage.js pour les fonctionnalités qui, elles,
// appellent réellement un modèle de langage (Rapport mensuel, Chat...).
function computeRealAnalysis(historicalData, forecastData, scenario, totalRevenueForecast, totalProfitForecast, currentCash) {
  const realGrowth = computeRealGrowthRate(historicalData);
  const trendPct = Math.round((realGrowth - 1) * 1000) / 10;
  const trendLabel = trendPct > 1 ? "en progression" : trendPct < -1 ? "en recul" : "stable";

  const lowestMonth = forecastData.reduce((min, d) => (d.profit < min.profit ? d : min), forecastData[0] || { profit: 0, month: "—" });
  const negativeMonths = forecastData.filter(d => d.profit < 0);
  const cashAtLowPoint = currentCash + forecastData.slice(0, forecastData.indexOf(lowestMonth) + 1).reduce((s, d) => s + d.profit, 0);

  const risks = [];
  if (negativeMonths.length > 0) {
    risks.push({ level: "élevé", text: `${negativeMonths.length} mois sur ${forecastData.length} projette un résultat négatif (le plus marqué : ${lowestMonth.month}, ${fmt(lowestMonth.profit)} €). Vérifiez la couverture de trésorerie sur cette période.` });
  } else if (cashAtLowPoint < currentCash * 0.5) {
    risks.push({ level: "moyen", text: `La trésorerie projetée au point bas (${lowestMonth.month}) descend à ${fmt(cashAtLowPoint)} €, soit une baisse notable par rapport au solde actuel (${fmt(currentCash)} €).` });
  } else {
    risks.push({ level: "faible", text: `Aucun mois à résultat négatif détecté sur la période projetée — la trésorerie reste au-dessus de la moitié du solde actuel au point le plus bas (${lowestMonth.month}).` });
  }

  const opportunities = [];
  if (trendPct > 1) {
    opportunities.push({ text: `Le CA réel des ${historicalData.length} derniers mois progresse en moyenne de ${trendPct}%/mois — en le maintenant sur la période, le CA cumulé atteindrait ${fmt(totalRevenueForecast)} €.` });
  } else if (trendPct < -1) {
    opportunities.push({ text: `Le CA réel recule en moyenne de ${Math.abs(trendPct)}%/mois sur l'historique disponible — une action commerciale ciblée sur ce recul aurait un effet direct sur la projection.` });
  } else {
    opportunities.push({ text: `Le CA réel est stable sur l'historique disponible (${historicalData.length} mois) — une croissance, même modeste, améliorerait sensiblement le résultat cumulé projeté (${fmt(totalProfitForecast)} €).` });
  }

  return {
    summary: `Sur la base des ${historicalData.length} derniers mois réels (tendance ${trendLabel}, ${trendPct >= 0 ? "+" : ""}${trendPct}%/mois), le scénario ${scenario} projette un CA de ${fmt(totalRevenueForecast)} € et un résultat de ${fmt(totalProfitForecast)} € sur la période choisie.`,
    risks,
    opportunities,
    recommendation: negativeMonths.length > 0
      ? `Anticiper un besoin de trésorerie avant ${lowestMonth.month} — le résultat cumulé devient négatif ce mois-là dans ce scénario.`
      : `Maintenir la tendance actuelle (${trendLabel}) ; aucun signal de rupture de trésorerie détecté sur la période projetée dans ce scénario.`,
  };
}

export default function Forecast({ currentCompanyId, currentSociete, onNavigate }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("forecast");
  const [scenario, setScenario] = useState("realiste");
  const [horizonMonths, setHorizonMonths] = useState(6);
  const [dashData, setDashData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(0);

  const call = useCallback(async (path) => {
    const res = await fetch(path, { headers: { Authorization: "Bearer " + getToken() } });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      call(`/api/dashboard?company_id=${currentCompanyId}`),
      // Historique réel (CA encaissé + dépenses réelles, 6 derniers mois) —
      // même endpoint que Dashboard.jsx, remplace l'ancienne fabrication.
      call(`/api/dashboard/chart/${currentCompanyId}`),
    ])
      .then(([dash, chart]) => { setDashData(dash); setChartData(chart); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentCompanyId, call]);

  // dashData.treasury n'existe pas côté backend (vrai champ : bank_balance,
  // voir server/routes/dashboard.js) — currentCash était silencieusement à 0
  // en permanence, y compris pour des sociétés avec un vrai solde bancaire.
  const currentCash = dashData?.bank_balance || 0;
  const historicalData = mapRealHistory(chartData);
  const forecastData = buildForecast(historicalData, scenario, horizonMonths);

  const combined = [
    ...historicalData.map(d => ({ ...d, type: "historique" })),
    ...forecastData.map(d => ({ ...d, type: "prévision" })),
  ];

  const totalRevenueForecast = forecastData.reduce((s, d) => s + d.revenue, 0);
  const totalProfitForecast = forecastData.reduce((s, d) => s + d.profit, 0);
  const avgMonthlyRevenue = totalRevenueForecast / (forecastData.length || 1);

  // Synthèse calculée pour de bon à partir des données réelles ci-dessus —
  // recalculée à chaque changement de scénario/horizon, pas un clic "Analyser"
  // séparé qui simulait un appel IA inexistant.
  const analysis = historicalData.length > 0
    ? computeRealAnalysis(historicalData, forecastData, scenario, totalRevenueForecast, totalProfitForecast, currentCash)
    : null;

  const TABS = [
    { id: "forecast",  label: "🔮 Prévision 6 mois" },
    { id: "scenarios", label: "📐 Scénarios" },
    { id: "ia",        label: "📋 Synthèse" },
    { id: "alertes",   label: "🔔 Alertes" },
  ];

  if (loading && !dashData) {
    return <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>Chargement des données réelles…</div>;
  }

  return (
    <div style={{ padding: "0 0 40px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>🔮 Prévision trésorerie</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={horizonMonths} onChange={e => setHorizonMonths(Number(e.target.value))} style={{ ...S.input, width: 120 }}>
            <option value={3}>3 mois</option>
            <option value={6}>6 mois</option>
            <option value={12}>12 mois</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {/* ── Forecast ── */}
      {tab === "forecast" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "CA projeté " + horizonMonths + " mois", value: fmt(totalRevenueForecast) + " €", color: "#1a9e72" },
              { label: "Résultat projeté", value: fmt(totalProfitForecast) + " €", color: totalProfitForecast >= 0 ? "#1a9e72" : "#ef4444" },
              { label: "CA moyen mensuel", value: fmt(avgMonthlyRevenue) + " €", color: "#185fa5" },
              { label: "Scénario actif", value: scenario.charAt(0).toUpperCase() + scenario.slice(1), color: scenario === "optimiste" ? "#1a9e72" : scenario === "pessimiste" ? "#ef4444" : "#185fa5" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>
              Historique + Prévision — CA et Résultat (€)
              <span style={{ marginLeft: 10, fontSize: 11, color: "#4b5563" }}>/ les 6 derniers mois (réel) + {horizonMonths} mois (prévu)</span>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={combined} margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Legend />
                <ReferenceLine x={historicalData[historicalData.length - 1]?.month} stroke="#e5e7eb" strokeDasharray="4 4" label={{ value: "Aujourd'hui", fill: "#4b5563", fontSize: 10 }} />
                <Bar dataKey="revenue" name="CA HT" fill="#1a9e72" radius={[2, 2, 0, 0]} opacity={0.85} />
                <Bar dataKey="expenses" name="Charges" fill="#f59e0b" radius={[2, 2, 0, 0]} opacity={0.7} />
                <Line type="monotone" dataKey="profit" name="Résultat" stroke="#185fa5" strokeWidth={2} dot={false} strokeDasharray="4 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...S.card, marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Détail mensuel — Prévisions</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                {["Mois", "CA prévu", "Fourchette basse", "Fourchette haute", "Charges", "Résultat"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {forecastData.map((d, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "8px 12px", color: "#4b5563", fontWeight: 600 }}>{d.month}</td>
                    <td style={{ padding: "8px 12px", color: "#1a9e72", fontWeight: 700 }}>{fmt(d.revenue)} €</td>
                    <td style={{ padding: "8px 12px", color: "#4b5563" }}>{fmt(d.lower)} €</td>
                    <td style={{ padding: "8px 12px", color: "#4b5563" }}>{fmt(d.upper)} €</td>
                    <td style={{ padding: "8px 12px", color: "#f59e0b" }}>{fmt(d.expenses)} €</td>
                    <td style={{ padding: "8px 12px", fontWeight: 800, color: d.profit >= 0 ? "#1a9e72" : "#ef4444" }}>{fmt(d.profit)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Scenarios ── */}
      {tab === "scenarios" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
            {[["optimiste", "#1a9e72", "📈 +15%", "Croissance forte, récupération de tous les prospects"],
              ["realiste",  "#185fa5", "📊 Base", "Tendance actuelle maintenue, légère amélioration"],
              ["pessimiste","#ef4444", "📉 -15%", "Ralentissement économique, pression sur les marges"]
            ].map(([s, c, title, desc]) => (
              <div key={s} onClick={() => setScenario(s)} style={{ ...S.card, flex: 1, cursor: "pointer", border: `2px solid ${scenario === s ? c : "#e5e7eb"}`, background: scenario === s ? c + "11" : "#ffffff" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c, marginBottom: 6 }}>{title}</div>
                <div style={{ color: "#111827", fontWeight: 700, textTransform: "capitalize", marginBottom: 4 }}>{s}</div>
                <div style={{ color: "#4b5563", fontSize: 12 }}>{desc}</div>
                {scenario === s && <div style={{ marginTop: 8, fontSize: 11, color: c, fontWeight: 700 }}>✓ Scénario actif</div>}
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Comparaison des 3 scénarios — CA projeté (€)</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" type="category" allowDuplicatedCategory={false} tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Legend />
                {[["optimiste", "#1a9e72"], ["realiste", "#185fa5"], ["pessimiste", "#ef4444"]].map(([s, c]) => {
                  const d = buildForecast(historicalData, s, horizonMonths);
                  return <Line key={s} data={d} type="monotone" dataKey="revenue" name={s.charAt(0).toUpperCase() + s.slice(1)} stroke={c} strokeWidth={2} dot={false} strokeDasharray={s === "realiste" ? "0" : "5 3"} />;
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 20 }}>
            {[["optimiste", "#1a9e72"], ["realiste", "#185fa5"], ["pessimiste", "#ef4444"]].map(([s, c]) => {
              const d = buildForecast(historicalData, s, horizonMonths);
              const totalCA = d.reduce((sum, r) => sum + r.revenue, 0);
              const totalProfit = d.reduce((sum, r) => sum + r.profit, 0);
              return (
                <div key={s} style={{ ...S.card, border: `1px solid ${c}33` }}>
                  <div style={{ fontWeight: 700, color: c, fontSize: 14, marginBottom: 12, textTransform: "capitalize" }}>{s}</div>
                  <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>CA total {horizonMonths}m</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: c, marginBottom: 8 }}>{fmt(totalCA)} €</div>
                  <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>Résultat total</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: totalProfit >= 0 ? "#1a9e72" : "#ef4444" }}>{fmt(totalProfit)} €</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Synthèse — calcul réel, pas un appel IA ── */}
      {tab === "ia" && (
        <div>
          <div style={{ background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.25)", borderRadius: 12, padding: 16, marginBottom: 20, display: "flex", gap: 16, alignItems: "flex-start" }}>
            <span style={{ fontSize: 28 }}>📋</span>
            <div>
              <div style={{ color: "#185fa5", fontWeight: 700, marginBottom: 4 }}>Synthèse calculée</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>Calculée directement à partir de vos {historicalData.length} derniers mois réels (CA encaissé, dépenses) et du scénario sélectionné — pas de génération par IA.</div>
            </div>
          </div>

          {analysis ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#185fa5", marginBottom: 8 }}>📋 Synthèse</div>
                <div style={{ color: "#111827", fontSize: 13 }}>{analysis.summary}</div>
              </div>

              <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#f59e0b", marginBottom: 12 }}>⚠️ Risques identifiés</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {analysis.risks.map((r, i) => (
                    <div key={i} style={{ padding: 12, background: r.level === "élevé" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)", borderRadius: 8, border: `1px solid ${r.level === "élevé" ? "rgba(239,68,68,0.3)" : "rgba(245,158,11,0.3)"}` }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: r.level === "élevé" ? "#ef4444" : "#f59e0b", marginRight: 8, textTransform: "uppercase" }}>{r.level}</span>
                      <span style={{ color: "#111827", fontSize: 13 }}>{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1a9e72", marginBottom: 12 }}>✅ Constats</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {analysis.opportunities.map((o, i) => (
                    <div key={i} style={{ padding: 12, background: "rgba(26,158,114,0.1)", borderRadius: 8, border: "1px solid rgba(26,158,114,0.3)", color: "#111827", fontSize: 13 }}>💡 {o.text}</div>
                  ))}
                </div>
              </div>

              <div style={{ ...S.card, background: "rgba(24,95,165,0.08)", border: "1px solid rgba(24,95,165,0.25)" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#185fa5", marginBottom: 8 }}>🎯 Recommandation</div>
                <div style={{ color: "#111827", fontSize: 13 }}>{analysis.recommendation}</div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 60, color: "#4b5563" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 14 }}>Aucun historique réel disponible pour cette société — la synthèse nécessite au moins quelques mois de factures/dépenses enregistrées.</div>
            </div>
          )}
        </div>
      )}

      {/* ── Alertes ── */}
      {tab === "alertes" && (
        <div>
          <div style={{ ...S.card, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Configurer les alertes de trésorerie</div>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 6 }}>Seuil minimum de trésorerie (€)</label>
                <input type="number" value={alertThreshold} onChange={e => setAlertThreshold(Number(e.target.value))} style={S.input} placeholder="Ex: 10000" />
              </div>
              <button onClick={() => alert("Alerte configurée !")} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>✅ Enregistrer</button>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Alertes actives</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[
                { icon: "💰", title: "Trésorerie faible", desc: `Alerte si solde < ${fmt(alertThreshold || 10000)} €`, active: true, color: "#ef4444" },
                { icon: "📉", title: "Résultat mensuel négatif", desc: "Alerte si résultat du mois < 0 €", active: true, color: "#f59e0b" },
                { icon: "📈", title: "Objectif CA dépassé", desc: "Notification si CA mensuel > objectif", active: false, color: "#1a9e72" },
                { icon: "⏰", title: "Factures en retard", desc: "Alerte si impayés > 30 jours", active: true, color: "#f59e0b" },
              ].map(a => (
                <div key={a.title} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 16px", background: "#f9fafb", borderRadius: 10, border: `1px solid ${a.active ? a.color + "33" : "#f3f4f6"}` }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{a.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#111827", fontWeight: 700, fontSize: 13 }}>{a.title}</div>
                    <div style={{ color: "#4b5563", fontSize: 12 }}>{a.desc}</div>
                  </div>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: a.active ? a.color + "22" : "rgba(100,116,139,0.2)", color: a.active ? a.color : "#4b5563" }}>
                    {a.active ? "Actif" : "Inactif"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
